import React, {useMemo} from "react"
import {useTranslation} from "react-i18next"

import {StudentStatus} from "types/students"
import {KlassDropdown} from "components/Select"
import {handleError, StatusStateShortName} from "helpers"
import {Auth} from "types/auth"
import {useGroupedStudentStatusesByStateAsDropdownOptions} from "./useGroupedStudentStatusesByStateAsDropdownOptions"
import {useQuery} from "@tanstack/react-query"
import {retentionService} from "services"

type NewStudentStatusSelectProps = {
    forState?: Auth.StudentState[]
    error?: boolean
    isClearable?: boolean
    isRetentionStatus?: boolean
} & (
    | {
          isMulti: true
          value?: StudentStatus[]
          onChange: (status: StudentStatus[]) => void
      }
    | {
          isMulti?: false
          value?: StudentStatus | null
          onChange: (status: StudentStatus | null) => void
      }
)

export const NewStudentStatusSelectFilter = ({
    forState,
    error = false,
    isMulti = false,
    value,
    isClearable,
    isRetentionStatus,
    onChange
}: NewStudentStatusSelectProps) => {
    const {t} = useTranslation(["user"])

    const {data: stageMapping} = useQuery(["retention-stage-settings"], () => retentionService.getAcademicStage(), {
        onError: (err) => {
            handleError(err)
        },
        enabled: isRetentionStatus,
        initialData: {}
    })

    const statusIds = useMemo(() => {
        if (!isRetentionStatus) return []

        return Object.values(stageMapping)
            .flat()
            .map((item) => item.statusId)
    }, [stageMapping, isRetentionStatus])

    const {groupedStatuses} = useGroupedStudentStatusesByStateAsDropdownOptions({forState, statusIds})

    return (
        <KlassDropdown
            error={error}
            options={groupedStatuses}
            value={value}
            valueKey="statusId"
            labelKey="name"
            onChange={onChange}
            isClearable={isClearable}
            isMulti={isMulti}
            placeholder={t("user.studentStatus")}
            getOptionLabel={(option: StudentStatus) => {
                if (!option) return ""

                return `[${StatusStateShortName[option.state]}] ${option.name}`
            }}
        />
    )
}
